import { Vec3, length, normalize, scale } from '../core/math/vec3'
import { unrotate } from '../core/math/quat'
import { attitudeOf, navballProject } from '../core/nav/attitude'
import { surfaceVelocity } from '../core/nav/readouts'
import { densityAt } from '../core/world/atmosphere'
import { altitudeOf } from '../core/dynamics/flight'
import { Simulation, SimVessel } from '../game/simulation'

const CIELO = '#2f6ea8'
const TERRA = '#7a5a34'
const ORIZZONTE = '#e8eef4'
const TACCA = 'rgba(232, 238, 244, 0.75)'

export interface Navball {
  update(sim: Simulation, sv: SimVessel): void
}

/**
 * Navball disegnata su canvas 2D invece che modellata come sfera.
 *
 * Una sfera vera non aggiungerebbe informazione: quello che il pilota legge
 * sono l'inclinazione dell'orizzonte, la scala del beccheggio e la posizione
 * dei marcatori. Disegnarla costa una mesh in meno e resta nitida a ogni
 * risoluzione.
 */
export function createNavball(canvas: HTMLCanvasElement): Navball {
  const ctx = canvas.getContext('2d')!
  const cx = canvas.width / 2
  const cy = canvas.height / 2
  const R = Math.min(cx, cy) - 12

  function marcatore(p: { x: number; y: number; visible: boolean }, colore: string, retro: boolean): void {
    if (!p.visible) return
    const x = cx + p.x * R
    const y = cy - p.y * R
    ctx.strokeStyle = colore
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(x, y, 6, 0, Math.PI * 2)
    ctx.stroke()
    ctx.beginPath()
    if (retro) {
      // retrograda: una X dentro il cerchio
      ctx.moveTo(x - 4.5, y - 4.5); ctx.lineTo(x + 4.5, y + 4.5)
      ctx.moveTo(x + 4.5, y - 4.5); ctx.lineTo(x - 4.5, y + 4.5)
    } else {
      // prograda: tre raggi e un punto
      ctx.moveTo(x - 11, y); ctx.lineTo(x - 6, y)
      ctx.moveTo(x + 6, y); ctx.lineTo(x + 11, y)
      ctx.moveTo(x, y - 11); ctx.lineTo(x, y - 6)
    }
    ctx.stroke()
    ctx.fillStyle = colore
    ctx.beginPath()
    ctx.arc(x, y, 1.6, 0, Math.PI * 2)
    ctx.fill()
  }

  return {
    update(sim, sv) {
      const att = attitudeOf(sv.state.position, sv.state.orientation)
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      ctx.save()
      ctx.beginPath()
      ctx.arc(cx, cy, R, 0, Math.PI * 2)
      ctx.clip()

      // la sfera ruota col rollio e scorre col beccheggio
      ctx.translate(cx, cy)
      ctx.rotate(att.roll)
      const scorrimento = (att.pitch / (Math.PI / 2)) * R

      ctx.fillStyle = CIELO
      ctx.fillRect(-R * 2, -R * 2, R * 4, R * 2 + scorrimento)
      ctx.fillStyle = TERRA
      ctx.fillRect(-R * 2, scorrimento, R * 4, R * 2)

      ctx.strokeStyle = ORIZZONTE
      ctx.lineWidth = 1.6
      ctx.beginPath()
      ctx.moveTo(-R * 2, scorrimento)
      ctx.lineTo(R * 2, scorrimento)
      ctx.stroke()

      // scala del beccheggio ogni 10 gradi
      ctx.strokeStyle = TACCA
      ctx.fillStyle = TACCA
      ctx.lineWidth = 1
      ctx.font = '9px ui-monospace, monospace'
      ctx.textAlign = 'center'
      for (let g = -80; g <= 80; g += 10) {
        if (g === 0) continue
        const y = scorrimento - (g / 90) * R
        if (Math.abs(y) > R) continue
        const largo = g % 30 === 0 ? R * 0.34 : R * 0.17
        ctx.beginPath()
        ctx.moveTo(-largo, y)
        ctx.lineTo(largo, y)
        ctx.stroke()
        if (g % 30 === 0) ctx.fillText(String(g), largo + 12, y + 3)
      }
      ctx.restore()

      // marcatori: direzioni portate in sistema corpo e proiettate sul disco
      const inAria = densityAt(sim.body.atmosphere, altitudeOf(sv.state.position, sim.body)) > 0
      const v: Vec3 = inAria ? surfaceVelocity(sv.state, sim.body) : sv.state.velocity
      if (length(v) > 1) {
        const corpo = unrotate(sv.state.orientation, normalize(v))
        marcatore(navballProject(corpo), '#7fd6a0', false)
        marcatore(navballProject(scale(corpo, -1)), '#ffb3a7', true)
      }

      // reticolo fisso: dove punta il muso
      ctx.strokeStyle = '#ffd166'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(cx - 18, cy); ctx.lineTo(cx - 6, cy)
      ctx.moveTo(cx + 6, cy); ctx.lineTo(cx + 18, cy)
      ctx.moveTo(cx, cy - 6); ctx.lineTo(cx, cy - 14)
      ctx.stroke()

      // cornice e rotta
      ctx.strokeStyle = 'rgba(120, 160, 200, 0.5)'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(cx, cy, R, 0, Math.PI * 2)
      ctx.stroke()

      ctx.fillStyle = '#d7e2ec'
      ctx.font = '11px ui-monospace, monospace'
      ctx.textAlign = 'center'
      ctx.fillText(`${Math.round((att.heading * 180) / Math.PI)}°`, cx, 12)
    },
  }
}
